package com.eladfinish.bible.contest.screens.search.ui

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.foundation.Image
import androidx.compose.foundation.combinedClickable
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowDropDown
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.platform.LocalHapticFeedback
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.constants.StringConstants.HASH_TAG_SYMBOL
import com.eladfinish.bible.contest.ic_trash
import com.eladfinish.bible.contest.ui.inputs.AppTextField
import dev.icerock.moko.resources.compose.localized
import dev.icerock.moko.resources.compose.painterResource
import dev.icerock.moko.resources.desc.Raw
import dev.icerock.moko.resources.desc.StringDesc

@Composable
fun SearchInput(
    inputLabel: StringDesc,
    onDone: () -> Unit,
    expression: TextFieldValue,
    onValueChange: (TextFieldValue) -> Unit,
    onArrowClick: () -> Unit,
    onArrowLongClick: (Boolean) -> Unit,
    isExpanded: Boolean,
    onClearClick: () -> Unit,
    onClearLongClick: () -> Unit,
    errorMessage: StringDesc? = null,
    modifier: Modifier = Modifier,
) {
    val currentKeyboardType = remember(expression.text) {
        if (expression.text.startsWith(HASH_TAG_SYMBOL)) {
            KeyboardType.Number
        } else {
            KeyboardType.Text
        }
    }

    AppTextField(
        value = expression,
        onValueChange = onValueChange,
        label = inputLabel.localized(),
        keyboardType = currentKeyboardType,
        onImeAction = onDone,
        leadingIcon = {
            SearchExpandButton(
                isExpanded = isExpanded,
                onClick = onArrowClick,
                onLongClick = {
                    onArrowLongClick(isExpanded)
                },
            )
        },
        trailingIcon = {
            if (expression.text.isNotEmpty()) {
                SearchClearButton(
                    onClick = {
                        onClearClick()
                        onValueChange(TextFieldValue())
                    },
                    onLongClick = onClearLongClick,
                )
            }
        },
        errorMessage = errorMessage?.localized(),
        modifier = modifier,
    )
}

@Composable
private fun SearchExpandButton(
    isExpanded: Boolean,
    onClick: () -> Unit,
    onLongClick: () -> Unit,
) {
    val haptic = LocalHapticFeedback.current
    val degrees = animateFloatAsState(
        targetValue = if (isExpanded) 180f else 0f,
    )

    Image(
        imageVector = Icons.Filled.ArrowDropDown,
        contentDescription = "Expand/Collapse",
        modifier = Modifier
            .size(36.dp)
            .rotate(degrees = degrees.value)
            .clip(RoundedCornerShape(42.dp))
            .combinedClickable(
                onClick = onClick,
                onLongClick = {
                    haptic.performHapticFeedback(HapticFeedbackType.LongPress)
                    onLongClick()
                },
            ),
        alpha = 0.6f,
    )
}

@Composable
private fun SearchClearButton(
    onClick: () -> Unit,
    onLongClick: () -> Unit,
) {
    val haptic = LocalHapticFeedback.current

    Image(
        painter = painterResource(SharedRes.images.ic_trash),
        contentDescription = "Clear",
        modifier = Modifier
            .size(36.dp)
            .clip(RoundedCornerShape(42.dp))
            .combinedClickable(
                onClick = onClick,
                onLongClick = {
                    haptic.performHapticFeedback(HapticFeedbackType.LongPress)
                    onLongClick()
                },
            ),
        alpha = 0.6f,
    )
}

@Preview(showBackground = true)
@Composable
private fun SearchInputPreview() {
    var expression by remember {
        mutableStateOf(
            TextFieldValue("בראשית")
        )
    }

    MaterialTheme {
        SearchInput(
            inputLabel = StringDesc.Raw("חיפוש"),
            onDone = {},
            expression = expression,
            onValueChange = { expression = it },
            onArrowClick = {},
            onArrowLongClick = {},
            isExpanded = false,
            onClearClick = {},
            onClearLongClick = {},
            errorMessage = null,
        )
    }
}
